import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonChip, IonLabel, IonRow } from '@ionic/react'
import { useTranslation } from 'react-i18next'

import { ToggleSegmentButtons } from '../../../../../components/segmentButtons/toggleSegmentButtons/ToggleSegmentButtons.component'
import SvgRenderer from '../../../../../components/svgRenderer/svgRenderer.component'
import Chips from '../../../../../components/chips/Chips.component'
import { ISegmentButton } from '../../../../../components/segmentButtons/SegmentButtons.component'
import { IBodyParts } from '../store/bodySurvey.types'
import ChipComponent from '../components/Chips.component'
import { IDataPointsConfig } from '../../../../configurations/store/configurations.types'
import { getDatapointsConfigSelector } from '../../../../configurations/selectors'
import { IOptions } from '../../../../../common/common.types'

import chestLungData from '../data/chestLung.json'
import posteriorChestLungData from '../data/posteriorChestLung.json'
import abdomenData from '../data/abdomen.json'
import rightFootData from '../data/rightFoot.json'
import leftFootData from '../data/leftFoot.json'
import rightHandData from '../data/rightHand.json'
import leftHandData from '../data/leftHand.json'
import faceData from '../data/face.json'
import { setChestLungMode } from '../store/bodySurvey.actions'
import { getBodySurveySelector } from '../selectors'
import { getDocumentedLocations } from '../../../../../utilities/bodySurvey.util'

interface ZoomedInBodySectionProps {
  savedBodyLocations: IOptions
  tempBodyParts: IBodyParts[]
  zoomedPart: IBodyParts | null
  onBodyPartSelection: (bodyVal: IBodyParts) => void
  chestLungMode?: ISegmentButton
}

export const chestLungModeData: ISegmentButton[] = [
  { id: 1, name: 'Front' },
  { id: 2, name: 'Back' },
]

const ZoomEnabledBodyParts: Record<number, { svgData: any[]; attributes: { viewBox: string } }> = {
  5: {
    svgData: [chestLungData, posteriorChestLungData],
    attributes: { viewBox: '0 0 299 195' },
  },
  3: { svgData: [faceData], attributes: { viewBox: '0 0 299 346' } },
  19: { svgData: [abdomenData], attributes: { viewBox: '0 0 247 349' } },
  93: { svgData: [rightHandData], attributes: { viewBox: '0 0 191 253' } },
  94: { svgData: [leftHandData], attributes: { viewBox: '0 0 191 253' } },
  95: { svgData: [rightFootData], attributes: { viewBox: '0 0 124 277' } },
  96: { svgData: [leftFootData], attributes: { viewBox: '0 0 124 277' } },
}

const BodyZoomView: React.FC<ZoomedInBodySectionProps> = ({
  savedBodyLocations,
  zoomedPart,
  onBodyPartSelection,
  tempBodyParts,
  chestLungMode = { id: 1, name: 'Front' },
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const dataPointsConfig: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_INTRATHORACIC } = dataPointsConfig

  const bodySurveyState = useSelector(getBodySurveySelector)

  const { examValues, examValuesPNNVs, findings } = bodySurveyState
  const posteriorViewIds = [101, 102, 106]

  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocationIds = getDocumentedLocations(combinedExamOptions, findings).map((location) => location.id)
  const isPosteriorViewDataPresent = posteriorViewIds.some((posteriorId) =>
    allDocumentedLocationIds.includes(posteriorId)
  )

  useEffect(() => {
    if (!isPosteriorViewDataPresent && DP_INTRATHORACIC === 0) {
      dispatch(setChestLungMode({ id: 1, name: 'Front' }))
    }
  }, [isPosteriorViewDataPresent])

  if (!zoomedPart || !zoomedPart.id) return null

  const { svgData, attributes } = ZoomEnabledBodyParts[zoomedPart.id] || {
    svgData: [null],
    attributes: { viewBox: '0 0 124 277' },
  }

  const renderToggleButtons = () => {
    if (zoomedPart.id === 5 && (DP_INTRATHORACIC === 1 || isPosteriorViewDataPresent)) {
      return (
        <div className='chest-lung-toggle mb-2'>
          <ToggleSegmentButtons
            selected={chestLungMode}
            segmentsList={chestLungModeData}
            dataTestId='ChestLungToggle'
            onChangeAction={(option) => dispatch(setChestLungMode(option))}
          />
        </div>
      )
    }
    return (
      <IonChip outline className='body-survey-chip body-survey-chip-size'>
        <IonLabel className='body-survey-chip-label'>{t('additionalAssessments.bodySurvey.front')}</IonLabel>
      </IonChip>
    )
  }

  const getFillColor = (svg: IBodyParts): string => {
    const savedBodyPartIds = savedBodyLocations.map((part) => part.id)
    const selectedBodyPartIds = tempBodyParts.map((part) => part.id)
    const examinedBodyParts = tempBodyParts.filter((part) => part.isExamined).map((val) => val.id)

    const isExamined = examinedBodyParts.includes(svg.id)
    const isSaved = savedBodyPartIds.includes(svg.id)
    const isSelected = selectedBodyPartIds.includes(svg.id)

    if (isExamined && isSaved) return 'var(--ion-color-zoll-lime-green)'
    if (svg.isDisabled) return 'var(--ion-color-zoll-gray)'
    if (isSaved && !isSelected) return 'var(--ion-color-zoll-lime-green)'
    if (isSelected) return 'var(--ion-color-zoll-blue-light)'

    return 'var(--ion-color-zoll-light-smoke)'
  }

  const setCustomClassnameToChips = (
    currentSvgData: IBodyParts[],
    tempBodyParts: IBodyParts[],
    savedBodyLocations: IOptions
  ) => {
    const chipsDataForSvgs = currentSvgData.filter((obj) => obj.id !== undefined && !obj.isDisabled)
    const locationFindings = tempBodyParts.map((part) => part.id)
    const savedBodyPartIds = savedBodyLocations.map((part) => part.id)
    const examinedBodyParts = tempBodyParts.filter((part) => part.isExamined).map((val) => val.id)

    return chipsDataForSvgs.map((chip) => {
      if (examinedBodyParts.includes(chip.id)) {
        return { ...chip, styles: 'body-part-chips-success' }
      }

      if (!locationFindings.includes(chip.id)) {
        if (savedBodyPartIds.includes(chip.id) && !locationFindings.includes(chip.id)) {
          return { ...chip, styles: 'body-part-chips-success' }
        }
        return { ...chip, styles: 'body-part-chips-outline' }
      }
      return chip
    })
  }

  const containerClassName = (() => {
    switch (zoomedPart.id) {
      case 5:
        if (chestLungMode.id === 1) {
          return 'chest-lung-svg-container'
        } else {
          return 'posterior-chest-lung-svg-container'
        }
      case 3:
        return 'face-svg-container'
      case 19:
        return 'abdomen-svg-container'
      case 93:
        return 'right-hand-svg-container'
      case 94:
        return 'left-hand-svg-container'
      case 95:
        return 'right-foot-svg-container'
      case 96:
        return 'left-foot-svg-container'
      default:
        return ''
    }
  })()

  const chipContainerClassNames = (() => {
    switch (zoomedPart.id) {
      case 3:
        return 'chip-container-face'
      case 19:
        return 'chip-container-abdomen'
      case 93:
        return 'chip-container-right-hand'
      case 94:
        return 'chip-container-left-hand'
      case 95:
        return 'chip-container-right-foot'
      case 96:
        return 'chip-container-right-foot'
      default:
        return ''
    }
  })()

  const currentSvgData: IBodyParts[] = zoomedPart.id === 5 && chestLungMode.id === 2 ? svgData[1] : svgData[0]
  const modifiedChipData = setCustomClassnameToChips(currentSvgData, tempBodyParts, savedBodyLocations)

  return (
    <div id='Zoom' className='body-container'>
      {renderToggleButtons()}
      <IonRow className='image-container mobile-nowrap'>
        {currentSvgData && (
          <div className='d-flex'>
            {![94, 96].includes(zoomedPart.id) && (
              <ChipComponent
                label='R'
                className={`${
                  zoomedPart.id === 5 && chestLungMode.id === 1 ? 'chest-lung-first-chip' : 'first-chip-zoom'
                }`}
              />
            )}
            {zoomedPart.id !== 5 && (
              <div className={`${chipContainerClassNames} mt-5 chip-container chip-content`}>
                <Chips
                  className='selectable-chips'
                  chipsArray={modifiedChipData}
                  handleChipDeletion={onBodyPartSelection}
                  handleClickOnChip={onBodyPartSelection}
                  showCrossBtn={false}
                />
              </div>
            )}
            <div className='svg-content'>
              <SvgRenderer
                className={`${containerClassName}`}
                svgData={currentSvgData}
                viewBox={attributes.viewBox}
                fill={getFillColor}
              />
            </div>
            {![93, 95].includes(zoomedPart.id) && (
              <ChipComponent
                label='L'
                className={`${
                  zoomedPart.id === 5 && chestLungMode.id === 1 ? 'chest-lung-second-chip' : 'second-chip-zoom'
                }`}
              />
            )}
          </div>
        )}
      </IonRow>
      {zoomedPart.id === 5 && (
        <div
          className={`${
            chestLungMode.id === 1 ? 'chip-container-chest-lung' : 'chip-container-posterior-chest'
          } chip-content`}>
          <Chips
            className='selectable-chips'
            chipsArray={modifiedChipData}
            handleChipDeletion={onBodyPartSelection}
            handleClickOnChip={onBodyPartSelection}
            showCrossBtn={false}
          />
        </div>
      )}
    </div>
  )
}

export default BodyZoomView
